// CommentMarkExtension.ts
// Uses ProseMirror decorations instead of marks to avoid conflicts with syntax highlighting
import { Extension, Editor } from "@tiptap/core"
import { Plugin, PluginKey } from "prosemirror-state"
import { Decoration, DecorationSet } from "prosemirror-view"

// Types
export interface CommentRange {
  commentId: string
  from: number
  to: number
}

interface CommentPluginState {
  ranges: CommentRange[]
  activeCommentId: string | null
}

interface CommentPluginMeta {
  action: "add" | "remove" | "setActive" | "sync"
  commentId?: string | null
  from?: number
  to?: number
  ranges?: CommentRange[]
}

export interface CommentMarkOptions {
  HTMLAttributes: Record<string, unknown>
  onCommentActivated?: (commentId: string | null) => void
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    comment: {
      /**
       * Set a comment decoration on the current selection
       */
      setComment: (commentId: string) => ReturnType
      /**
       * Remove comment decoration by ID or at current selection
       */
      unsetComment: (commentId?: string) => ReturnType
      /**
       * Sync comment ranges from external source
       */
      syncCommentRanges: (ranges: CommentRange[]) => ReturnType
      /**
       * Set the active comment
       */
      setActiveCommentDecoration: (commentId: string | null) => ReturnType
    }
  }
}

/**
 * Plugin key for comment decorations
 */
export const commentPluginKey = new PluginKey<CommentPluginState>(
  "commentDecoration"
)

/**
 * CommentMark - A TipTap Extension for attaching comments to text using decorations.
 *
 * This extension uses ProseMirror decorations instead of marks to avoid
 * conflicts with syntax highlighting decorations. Both systems can now
 * coexist and layer properly.
 *
 * Key behaviors:
 * - Decorations track text ranges and move with document changes
 * - When decorated text is deleted, the decoration is removed
 * - Clicking on decorated text activates the comment
 */
export const CommentMark = Extension.create<CommentMarkOptions>({
  name: "comment",

  addOptions() {
    return {
      HTMLAttributes: {},
      onCommentActivated: undefined,
    }
  },

  addCommands() {
    return {
      setComment:
        (commentId: string) =>
        ({ tr, state, dispatch }) => {
          const { from, to } = state.selection
          if (from === to) return false

          if (dispatch) {
            dispatch(
              tr.setMeta(commentPluginKey, {
                action: "add",
                commentId,
                from,
                to,
              } as CommentPluginMeta)
            )
          }
          return true
        },

      unsetComment:
        (commentId?: string) =>
        ({ tr, state, dispatch }) => {
          if (dispatch) {
            if (commentId) {
              // Remove specific comment
              dispatch(
                tr.setMeta(commentPluginKey, {
                  action: "remove",
                  commentId,
                } as CommentPluginMeta)
              )
            } else {
              // Remove comment at current selection
              const pluginState = commentPluginKey.getState(state)
              if (!pluginState) return false

              const { from, to } = state.selection
              const rangeToRemove = pluginState.ranges.find(
                (range) => range.from <= from && range.to >= to
              )

              if (rangeToRemove) {
                dispatch(
                  tr.setMeta(commentPluginKey, {
                    action: "remove",
                    commentId: rangeToRemove.commentId,
                  } as CommentPluginMeta)
                )
              }
            }
          }
          return true
        },

      syncCommentRanges:
        (ranges: CommentRange[]) =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            dispatch(
              tr.setMeta(commentPluginKey, {
                action: "sync",
                ranges,
              } as CommentPluginMeta)
            )
          }
          return true
        },

      setActiveCommentDecoration:
        (commentId: string | null) =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            dispatch(
              tr.setMeta(commentPluginKey, {
                action: "setActive",
                commentId,
              } as CommentPluginMeta)
            )
          }
          return true
        },
    }
  },

  addProseMirrorPlugins() {
    const { onCommentActivated } = this.options

    return [
      new Plugin<CommentPluginState>({
        key: commentPluginKey,

        state: {
          init(): CommentPluginState {
            return { ranges: [], activeCommentId: null }
          },

          apply(tr, oldState): CommentPluginState {
            const meta = tr.getMeta(commentPluginKey) as
              | CommentPluginMeta
              | undefined

            if (meta) {
              switch (meta.action) {
                case "add":
                  return {
                    ...oldState,
                    ranges: [
                      ...oldState.ranges,
                      {
                        commentId: meta.commentId!,
                        from: meta.from!,
                        to: meta.to!,
                      },
                    ],
                  }
                case "remove":
                  return {
                    ...oldState,
                    ranges: oldState.ranges.filter(
                      (r) => r.commentId !== meta.commentId
                    ),
                    activeCommentId:
                      oldState.activeCommentId === meta.commentId
                        ? null
                        : oldState.activeCommentId,
                  }
                case "setActive":
                  return { ...oldState, activeCommentId: meta.commentId || null }
                case "sync":
                  return { ...oldState, ranges: meta.ranges || [] }
              }
            }

            // Map ranges through document changes
            if (tr.docChanged) {
              const newRanges = oldState.ranges
                .map((range) => ({
                  commentId: range.commentId,
                  // Use -1 assoc for 'to' to prevent range from expanding when typing at the end
                  from: tr.mapping.map(range.from),
                  to: tr.mapping.map(range.to, -1),
                }))
                .filter((range) => range.from < range.to) // Remove collapsed ranges

              return { ...oldState, ranges: newRanges }
            }

            return oldState
          },
        },

        props: {
          decorations(state): DecorationSet {
            const pluginState = this.getState(state)
            if (!pluginState || pluginState.ranges.length === 0) {
              return DecorationSet.empty
            }

            const decorations = pluginState.ranges.map((range) => {
              const isActive = range.commentId === pluginState.activeCommentId
              return Decoration.inline(range.from, range.to, {
                class: isActive ? "comment-highlight active" : "comment-highlight",
                "data-comment-id": range.commentId,
              })
            })

            return DecorationSet.create(state.doc, decorations)
          },

          handleClick(view, pos): boolean {
            const pluginState = commentPluginKey.getState(view.state)
            if (!pluginState) return false

            // Find if click is within any comment range
            const clickedComment = pluginState.ranges.find(
              (range) => pos >= range.from && pos <= range.to
            )

            if (clickedComment) {
              // Set active comment via transaction
              view.dispatch(
                view.state.tr.setMeta(commentPluginKey, {
                  action: "setActive",
                  commentId: clickedComment.commentId,
                } as CommentPluginMeta)
              )

              if (onCommentActivated) {
                onCommentActivated(clickedComment.commentId)
              }
              return true
            }

            // Clicked outside any comment - deactivate
            if (pluginState.activeCommentId) {
              view.dispatch(
                view.state.tr.setMeta(commentPluginKey, {
                  action: "setActive",
                  commentId: null,
                } as CommentPluginMeta)
              )
              if (onCommentActivated) {
                onCommentActivated(null)
              }
            }

            return false
          },
        },
      }),
    ]
  },
})

/**
 * Helper function to find all comment ranges in the editor
 */
export const findAllCommentMarks = (
  editor: Editor
): { commentId: string; from: number; to: number; text: string }[] => {
  const pluginState = commentPluginKey.getState(editor.state)
  if (!pluginState) return []

  return pluginState.ranges.map((range) => ({
    commentId: range.commentId,
    from: range.from,
    to: range.to,
    text: editor.state.doc.textBetween(range.from, range.to, " "),
  }))
}

/**
 * Helper function to find a specific comment position
 */
export const findCommentMarkPosition = (
  editor: Editor,
  commentId: string
): { from: number; to: number; text: string } | null => {
  const pluginState = commentPluginKey.getState(editor.state)
  if (!pluginState) return null

  const range = pluginState.ranges.find((r) => r.commentId === commentId)
  if (!range) return null

  return {
    from: range.from,
    to: range.to,
    text: editor.state.doc.textBetween(range.from, range.to, " "),
  }
}

/**
 * Get unique comment IDs from the editor
 */
export const getCommentIdsInEditor = (editor: Editor): Set<string> => {
  const pluginState = commentPluginKey.getState(editor.state)
  if (!pluginState) return new Set()

  return new Set(pluginState.ranges.map((r) => r.commentId))
}

/**
 * Find text in document, optionally preferring matches near a specific line.
 * Used to re-apply comment decorations when loading comments from the backend.
 */
export const findTextInDocument = (
  editor: Editor,
  searchText: string,
  preferredLine?: number
): { from: number; to: number } | null => {
  const matches: { from: number; to: number; line: number }[] = []

  // Search through all text nodes
  editor.state.doc.descendants((node, pos) => {
    if (!node.isText || !node.text) return

    let searchIndex = 0
    let foundIndex = node.text.indexOf(searchText, searchIndex)

    while (foundIndex !== -1) {
      const from = pos + foundIndex
      const to = from + searchText.length

      // Calculate approximate line number (count block nodes before this position)
      let lineCount = 1
      editor.state.doc.nodesBetween(0, from, (n) => {
        if (n.isBlock) lineCount++
      })

      matches.push({ from, to, line: lineCount })

      searchIndex = foundIndex + 1
      foundIndex = node.text.indexOf(searchText, searchIndex)
    }
  })

  if (matches.length === 0) return null
  if (matches.length === 1) return { from: matches[0].from, to: matches[0].to }

  // Multiple matches - prefer the one closest to stored line
  if (preferredLine !== undefined) {
    matches.sort(
      (a, b) =>
        Math.abs(a.line - preferredLine) - Math.abs(b.line - preferredLine)
    )
  }

  return { from: matches[0].from, to: matches[0].to }
}

export default CommentMark
